import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowClockwise, Trash, Copy, Warning, UploadSimple, Receipt } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

/**
 * v1.24.5 — Duplicate UTR cleanup report.
 * Lists every UTR (or transaction reference) that appears on 2+ non-rejected
 * rows so admin can delete the offending duplicates and heal historical mess.
 */
export default function DuplicateUtrs() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/duplicate-utrs");
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const deleteRow = async (row) => {
        if (!window.confirm(`Delete this ${row.kind === "upload" ? "slip" : "transaction"} for ${row.party_name} (₹${row.amount.toLocaleString("en-IN")})?\n\nThis is permanent — pick the WRONG one, keep the correct one.`)) return;
        setBusy(row.id);
        try {
            if (row.kind === "upload") await api.delete(`/uploads/${row.id}`);
            else await api.delete(`/transactions/${row.id}`);
            toast.success("Deleted — group refreshed");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const groups = data?.groups || [];

    return (
        <div className="space-y-6" data-testid="duplicate-utrs-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-md">
                            <Copy size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Duplicate UTR Cleanup</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Every UTR appearing on 2+ non-rejected slips or transactions. Delete the wrong one, keep the correct one.</p>
                </div>
                <button onClick={load} disabled={loading} data-testid="duplicate-refresh"
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-100 disabled:opacity-50">
                    <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-rose-800">Duplicate groups</div>
                    <div className="mt-2 font-display text-2xl font-bold text-rose-900" data-testid="duplicate-count">{data?.count ?? "…"}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Rows involved</div>
                    <div className="mt-2 font-display text-2xl font-bold text-slate-900">
                        {groups.reduce((s, g) => s + g.rows.length, 0)}
                    </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800">Amount at risk</div>
                    <div className="mt-2 font-display text-2xl font-bold text-emerald-900">
                        {fmt(groups.reduce((s, g) => s + g.rows.reduce((x, r) => x + r.amount, 0), 0))}
                    </div>
                </div>
            </div>

            {groups.length === 0 && !loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    <EmptyState icon={Copy} title="No duplicates" description="Every UTR in the system is unique. The v1.24.4 hard-block is now keeping things clean." />
                </div>
            ) : (
                <div className="space-y-4">
                    {groups.map((g) => (
                        <div key={g.utr} data-testid={`dup-group-${g.utr}`} className="rounded-lg border border-rose-200 bg-white shadow-sm">
                            <div className="flex items-center justify-between border-b border-rose-100 bg-rose-50/60 px-4 py-2">
                                <div className="flex items-center gap-2">
                                    <Warning size={14} weight="fill" className="text-rose-700" />
                                    <span className="font-mono text-sm font-bold text-slate-900">{g.utr}</span>
                                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-800">
                                        {g.rows.length} copies
                                    </span>
                                </div>
                            </div>
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="px-3 py-2 text-left w-24">Kind</th>
                                        <th className="px-3 py-2 text-left">Party</th>
                                        <th className="px-3 py-2 text-left">Date</th>
                                        <th className="px-3 py-2 text-right">Amount</th>
                                        <th className="px-3 py-2 text-left">Status</th>
                                        <th className="px-3 py-2 text-right"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {g.rows.map((r) => (
                                        <tr key={r.id} data-testid={`dup-row-${r.id}`} className="hover:bg-slate-50">
                                            <td className="px-3 py-2">
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${r.kind === "upload" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                                                    {r.kind === "upload" ? <UploadSimple size={10} /> : <Receipt size={10} />}
                                                    {r.kind === "upload" ? "Slip" : "Txn"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <button onClick={() => navigate(`/admin/reports?partyId=${r.party_id}`)} className="text-blue-700 hover:underline">
                                                    {r.party_name}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-700">{r.date || "—"}</td>
                                            <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">{fmt(r.amount)}</td>
                                            <td className="px-3 py-2 text-xs text-slate-600">{r.status}</td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => deleteRow(r)}
                                                    disabled={busy === r.id}
                                                    data-testid={`dup-delete-${r.id}`}
                                                    className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-50 disabled:opacity-50"
                                                >
                                                    <Trash size={11} weight="bold" /> {busy === r.id ? "…" : "Delete"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
